// Analytics heartbeat hook to track active game sessions

import { useEffect, useRef } from 'react';
import { useRoomConfig } from './useRoomConfig';
import { useRoomMyPlayer } from './useRoomMyPlayer';
import { useRoomInteractionWindow } from './useRoomInteractionWindow';
import { analyticsService } from '../services/analyticsService';

/**
 * Hook that fires periodic analytics heartbeat events to keep sessions alive
 * in real-time analytics dashboards.
 *
 * Fires immediately on mount, then every 60 seconds while component is mounted.
 * Uses pure interval-based snapshots - not driven by room events.
 *
 * @param role - 'host' or 'player' to distinguish between host and player views
 */
export const useAnalyticsHeartbeat = (role: 'host' | 'player') => {
  const { roomCode, status, host, gameConfig } = useRoomConfig();
  const myPlayer = useRoomMyPlayer();
  const { windowType, windowStatus, windowId } = useRoomInteractionWindow();

  // Store current values in ref - updated on each render, read every 60s by interval
  const stateRef = useRef({
    roomCode,
    status,
    hostUserId: host?.userId,
    gameName: gameConfig?.gameName,
    gameId: gameConfig?.gameId,
    myPlayerId: myPlayer?.playerId,
    myPlayerName: myPlayer?.playerName,
    myTeamId: myPlayer?.teamId,
    windowId,
    windowType,
    windowStatus,
  });

  // Update ref on every render to ensure interval always reads fresh values
  stateRef.current = {
    roomCode,
    status,
    hostUserId: host?.userId,
    gameName: gameConfig?.gameName,
    gameId: gameConfig?.gameId,
    myPlayerId: myPlayer?.playerId,
    myPlayerName: myPlayer?.playerName,
    myTeamId: myPlayer?.teamId,
    windowId,
    windowType,
    windowStatus,
  };

  useEffect(() => {
    if (!roomCode) return;

    const trackHeartbeat = () => {
      const state = stateRef.current;
      analyticsService.trackEvent('game_activity_heartbeat', {
        room_code: state.roomCode,
        status: state.status,
        host_user_id: state.hostUserId,
        game_name: state.gameName,
        game_id: state.gameId,
        my_player_id: state.myPlayerId,
        my_player_name: state.myPlayerName,
        my_team_id: state.myTeamId,
        window_id: state.windowId,
        window_type: state.windowType,
        window_status: state.windowStatus,
        player_role: role,
      });
    };

    // Fire immediately on mount
    trackHeartbeat();

    // Then every 60 seconds (keeps GA4 sessions alive, 30min timeout)
    const interval = setInterval(trackHeartbeat, 60000);

    return () => clearInterval(interval);
  }, [role, roomCode]);
};
